import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    Paper,
    Switch,
    Divider,
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
    Stack,
    Avatar,
    alpha,
    useTheme,
    Fade,
    Grow,
    Tooltip,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    EditOutlined as EditIcon,
    LockPersonOutlined as LockPersonIcon,
    ContentCopyOutlined as CopyIcon,
    ExpandMore as ExpandMoreIcon,
    VerifiedUserOutlined as VerifiedIcon,
    ArrowBack as BackIcon,
    LibraryBooksOutlined as TradesIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import clgimg from '../Assests/clgimg.png';
import { getCollegeWithTrades } from '../Apis/CollegeApis';

const InfoItem = ({ label, value, copyable }) => (
    <Box sx={{ mb: 3 }}>
        <Typography variant="caption" fontWeight="800" color="#64748B" display="block" sx={{ mb: 0.5 }}>{label}</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" fontWeight="1000" color="#1E293B">{value || 'N/A'}</Typography>
            {copyable && value && (
                <Tooltip title="Copy to clipboard">
                    <IconButton size="small" sx={{ p: 0.5, color: '#2563EB', '&:hover': { bgcolor: alpha('#2563EB', 0.1) } }}>
                        <CopyIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    </Box>
);

const ProfileSidebar = ({ college }) => {
    const theme = useTheme();
    return (
        <Stack spacing={3}>
            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0', bgcolor: 'white' }}>
                <Box sx={{ display: 'flex', mb: 4 }}>
                    <Typography variant="caption" fontWeight="1000" sx={{ color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.08), px: 2, py: 0.5, borderRadius: 2 }}>
                        {college.code || 'CODE-NA'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', mb: 4 }}>
                    <Avatar
                        src={college.logo || clgimg}
                        variant="rounded"
                        sx={{ width: 100, height: 100, borderRadius: 5, mb: 3, border: '4px solid #F8FAFC', boxShadow: '0 15px 25px -5px rgba(0,0,0,0.05)' }}
                    />
                    <Typography variant="h5" fontWeight="1000" color="#0F172A" sx={{ mb: 0.5 }}>{college.name || 'Institution Title'}</Typography>
                    <Typography variant="body2" color="#64748B" fontWeight="800">{college.accreditation || 'Govt. Affiliated'} Institution</Typography>
                    <Chip
                        label={college.isActive ? "Validated Partner" : "Inactive Partner"}
                        size="small"
                        color={college.isActive ? "primary" : "default"}
                        sx={{ mt: 2, fontWeight: '1000', borderRadius: 2, fontSize: '0.65rem' }}
                    />
                </Box>

                <Divider sx={{ mb: 4, borderStyle: 'dashed' }} />

                <Stack spacing={1}>
                    <Typography variant="subtitle2" fontWeight="1000" color="#0F172A" sx={{ mb: 2 }}>Operational Metadata</Typography>
                    <InfoItem label="NCVTMIS CODE" value={college.code} copyable />
                    <InfoItem label="AUTHORIZED PRINCIPAL" value={college.principalName || 'Prof. Suresh Babu'} />
                </Stack>
            </Paper>

            <Paper elevation={0} sx={{ p: 4, borderRadius: 8, border: '1px solid #E2E8F0', bgcolor: 'white' }}>
                <Typography variant="subtitle2" fontWeight="1000" color="#0F172A" sx={{ mb: 2 }}>Compliance Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: alpha('#10B981', 0.05), borderRadius: 4, border: '1px solid', borderColor: alpha('#10B981', 0.1) }}>
                    <VerifiedIcon sx={{ color: '#10B981' }} />
                    <Box>
                        <Typography variant="body2" fontWeight="1000" color="#065F46">Fully Compliant</Typography>
                        <Typography variant="caption" color="#059669" fontWeight="800">Last audit: Aug 2025</Typography>
                    </Box>
                </Box>
            </Paper>
        </Stack>
    );
};

const TradesAndSubjects = ({ tradeMappings, loading }) => {
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                <CircularProgress size={40} thickness={5} />
            </Box>
        );
    }

    if (!tradeMappings || tradeMappings.length === 0) {
        return (
            <Paper elevation={0} sx={{ p: 5, borderRadius: 8, border: '1px solid #E2E8F0', textAlign: 'center', bgcolor: 'white' }}>
                <TradesIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 2 }} />
                <Typography variant="h6" fontWeight="1000" color="#475569">No Trades Available</Typography>
                <Typography variant="body2" color="#64748B">This institution hasn't registered any trades yet.</Typography>
            </Paper>
        );
    }

    return (
        <Stack spacing={3}>
            {tradeMappings.map((mapping, i) => {
                const tradeDetail = mapping.trade || {};
                return (
                    <Accordion key={i} elevation={0} sx={{
                        borderRadius: '20px !important',
                        border: '1px solid #E2E8F0',
                        '&:before': { display: 'none' },
                        mb: 2,
                        overflow: 'hidden'
                    }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack direction="row" spacing={3} alignItems="center">
                                <Avatar sx={{ bgcolor: '#F8FAFC', color: '#0F172A', border: '1px solid #E2E8F0' }}><TradesIcon fontSize="small" /></Avatar>
                                <Box>
                                    <Typography variant="body2" fontWeight="1000">{tradeDetail.name || 'Unnamed Trade'}</Typography>
                                    <Typography variant="caption" color="#64748B" fontWeight="800">Term: {tradeDetail.tradeYear || 'N/A'} • Level: {tradeDetail.level || 'N/A'}</Typography>
                                </Box>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ bgcolor: '#F8FAFC', px: 4, py: 3 }}>
                            <Typography variant="caption" fontWeight="1000" color="#64748B" display="block" sx={{ mb: 2 }}>TRADE INFORMATION</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                                        <Typography variant="caption" color="#64748B" fontWeight="800">Started On</Typography>
                                        <Typography variant="body2" fontWeight="1000">{mapping.startedAt ? new Date(mapping.startedAt).toLocaleDateString() : 'N/A'}</Typography>
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ p: 2, bgcolor: 'white', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                                        <Typography variant="caption" color="#64748B" fontWeight="800">Status</Typography>
                                        <Typography variant="body2" fontWeight="1000" color={mapping.isActive ? '#10B981' : '#EF4444'}>{mapping.isActive ? 'Active' : 'Inactive'}</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Stack>
    );
};

const CollageDetailed = () => {
    const [tradeMappings, setTradeMappings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [collegeData, setCollegeData] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();
    const college = location.state?.college || {};

    useEffect(() => {
        const fetchTradesData = async () => {
            if (!college.id) {
                setLoading(false);
                return;
            }
            try {
                const res = await getCollegeWithTrades(college.id);
                if (res?.success) {
                    setTradeMappings(res.data?.tradeMappings || []);
                    setCollegeData(res.data);
                }
            } catch (error) {
                console.error("Failed to fetch college trades:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchTradesData();
    }, [college.id]);

    const activeCollege = collegeData || college;
return (
        <Box sx={{ p: { xs: 2, md: 5 }, minHeight: '100vh', bgcolor: '#F8FAFC' }}>
            {/* Page Header */}
            <Fade in={true}>
                <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton onClick={() => navigate(-1)} sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: 3, '&:hover': { bgcolor: '#F1F5F9' } }}>
                            <BackIcon sx={{ color: '#0F172A' }} />
                        </IconButton>
                        <Box>
                            <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Institution Hub</Typography>
                            <Typography variant="body2" color="#64748B" fontWeight="600">Enterprise College Management Console</Typography>
                        </Box>
                    </Stack>
                </Box>
            </Fade>

            <Grid container spacing={5}>
                {/* Profile Section */}
                <Grid item size={{ xs: 12, lg: 4 }}>
                    <Grow in={true} timeout={600}>
                        <Box><ProfileSidebar college={activeCollege} /></Box>
                    </Grow>
                </Grid>

                {/* Analytical Content */}
                <Grid item size={{ xs: 12, lg: 8 }}>
                    <Fade in={true} timeout={1000}>
                        <Paper elevation={0} sx={{ p: 2, mb: 4, borderRadius: 6, border: '1px solid #E2E8F0', bgcolor: 'white', display: 'flex', alignItems: 'center', gap: 2 }}>
                            <TradesIcon sx={{ color: '#2563EB' }} />
                            <Typography variant="subtitle1" fontWeight="1000" color="#1E293B">Available Trades & Curriculum</Typography>
                        </Paper>
                    </Fade>

                    <Box sx={{ mt: 2 }}>
                        <Grow in={true}>
                            <Box>
                                <TradesAndSubjects tradeMappings={tradeMappings} loading={loading} />
                            </Box>
                        </Grow>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default CollageDetailed;
